import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'

// Default to "/api" (the Vite dev-server proxy / production rewrite). Setting
// VITE_API_BASE_URL to a full URL like http://localhost:8000 still works for
// people who don't want the proxy, but the proxy is preferred because it
// eliminates CORS as a class of bug — both in dev and (with a matching
// rewrite rule) in production.
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000, // user-initiated requests: be generous
})

// --- Request interceptor: forward Supabase JWT to the backend --------------
// We read the token synchronously from the zustand store rather than calling
// supabase.auth.getSession() each request. This avoids per-request latency
// and breaks the dependency on Supabase being reachable for every API call.
//
// The store's token is kept fresh by the Supabase auth-state listener in
// App.jsx, which fires on TOKEN_REFRESHED, SIGNED_IN, SIGNED_OUT, etc.
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) config.headers.Authorization = `Bearer ${token}`
  // Silent (background-polling) requests get a much shorter timeout. Dashboard
  // loads several optional endpoints in parallel; if any of them are slow or
  // hanging, we don't want them to hold the whole UI up for 30 seconds.
  if (config.silent === true && !config.timeout) {
    config.timeout = 6_000
  }
  return config
})

// --- Response interceptor: friendly error toasts + silent-mode support ----
//
// Toast policy:
//   - Each distinct error message is de-duplicated within a 4-second window.
//     Parallel page loads only show one toast per actual issue.
//   - Requests can opt out of toasts entirely by passing
//     `{ silent: true }` in the axios config. Dashboard polling and other
//     opportunistic background requests use this so backend gaps degrade
//     gracefully without scaring the user.
//   - Validation errors (FastAPI 422 detail array) are always shown so the
//     user knows what to fix when they submit a form.

const recentToasts = new Map() // message → timestamp
const TOAST_DEDUPE_MS = 4000

function showToastOnce(message) {
  const now = Date.now()
  const last = recentToasts.get(message) || 0
  if (now - last < TOAST_DEDUPE_MS) return
  recentToasts.set(message, now)
  toast.error(message)

  if (recentToasts.size > 50) {
    const cutoff = now - TOAST_DEDUPE_MS
    for (const [k, t] of recentToasts) {
      if (t < cutoff) recentToasts.delete(k)
    }
  }
}

function buildErrorMessage(err) {
  const status = err?.response?.status
  const detail = err?.response?.data?.detail

  if (Array.isArray(detail)) {
    return detail
      .map((d) => `${d.loc?.slice(1).join('.') || 'field'}: ${d.msg}`)
      .join(' • ')
  }
  if (typeof detail === 'string') return detail
  if (err?.code === 'ECONNABORTED') return 'Request timed out. Please try again.'
  if (err?.message === 'Network Error' || !err?.response) {
    return 'Cannot reach the backend right now. Please try again in a moment.'
  }
  if (status === 404) return "The backend doesn't support this action yet."
  if (status === 401) return 'Your session expired. Please sign in again.'
  if (status === 403) return "You don't have permission to do that."
  if (status >= 500) return 'The backend ran into an error. Please try again.'
  return 'Something went wrong'
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const silent = err?.config?.silent === true
    if (!silent) {
      showToastOnce(buildErrorMessage(err))
    }
    return Promise.reject(err)
  },
)
